import { type ReactNode, useState } from 'react';
import { Box, ButtonBase, Link as MuiLink, TableCell, TableRow, Typography } from '@mui/material';

import { moreTasksLabel, railsLine, SHOW_LESS_LABEL, SHOW_MORE_LABEL } from '../copy';
import { linkifyTokens, safeHttpUrl, taskBodyText } from '../tasks';
import type { FoldedPayer, OpenTaskView, PayerAudience, RailView } from '../types';

import { StateChip } from './StateChip';
import { StatusAgeLine } from './StatusAgeLine';
import { StediStatusList } from './StediStatusList';

export interface PayerRowProps {
  payer: FoldedPayer;
  audience?: PayerAudience;
  /**
   * The action cell's content. AC-7 (the "Open in Stedi" / "Submit first
   * claim" links) is GDN-3406's to wire — this component only renders
   * whatever it's given, or nothing. It sits under "what's happening", as
   * the design places it.
   */
  action?: ReactNode;
  /** Reference instant for the "Waiting" column; omit to leave the column empty. */
  asOf?: Date;
  /** A link inside the open task's instructions or attached to it was clicked (GDN-3992 tracking). */
  onTaskLinkClick?: (payer: FoldedPayer, source: TaskLinkSource) => void;
  /** The open task's Show more/Show less toggle was used; `expanded` is the new state. */
  onTaskToggled?: (payer: FoldedPayer, expanded: boolean) => void;
}

/** Where a task link came from: the instructions text, or Stedi's attached `links`. */
export type TaskLinkSource = 'inline' | 'attached';

const cellSx = {
  verticalAlign: 'top',
  borderColor: '#eeeef4',
  paddingTop: '15px',
  paddingBottom: '15px',
} as const;

const whatsHappeningSx = { fontSize: '13px', lineHeight: 1.55, color: '#33333d' } as const;

const taskLinkSx = { fontSize: 'inherit', fontWeight: 600, overflowWrap: 'anywhere' } as const;

/** Text nodes and anchors only — never HTML. Every anchor is http(s) and opens in a new tab. */
function LinkifiedText({ text, onLinkClick }: { text: string; onLinkClick?: () => void }) {
  return (
    <>
      {linkifyTokens(text).map((token, index) =>
        token.kind === 'link' ? (
          <MuiLink
            key={index}
            href={token.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onLinkClick}
            sx={taskLinkSx}
          >
            {token.text}
          </MuiLink>
        ) : (
          <span key={index}>{token.text}</span>
        ),
      )}
    </>
  );
}

/** A body longer than this shows clamped, with a toggle. Static so the toggle is deterministic. */
const CLAMP_LINES = 4;
const CLAMP_CHARS = 280;

function needsClamp(body: string): boolean {
  return body.split('\n').length > CLAMP_LINES || body.length > CLAMP_CHARS;
}

/**
 * The open task's copy (GDN-3992): the summary line, then the instructions
 * after the title with line breaks kept and URLs clickable, plus the links
 * Stedi attached that the text does not already contain, and how many other
 * open tasks the rail has. Whichever of summary and body carries the long
 * text is the one that clamps behind the toggle, so a single-paragraph task
 * never renders unbounded.
 */
function OpenTaskCopy({
  summary,
  task,
  onLinkClick,
  onToggle,
}: {
  summary: string;
  task: OpenTaskView;
  onLinkClick?: (source: TaskLinkSource) => void;
  onToggle?: (expanded: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const body = taskBodyText(task.instructions);
  const clampable = needsClamp(body || summary);
  const clampSx =
    clampable && !expanded
      ? {
          display: '-webkit-box',
          WebkitLineClamp: CLAMP_LINES,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : undefined;
  const attachedLinks = task.links.flatMap(link => {
    const href = safeHttpUrl(link.url);
    if (!href || task.instructions.includes(link.url)) return [];
    return [{ href, label: link.label?.trim() || link.url }];
  });

  return (
    <Box>
      <Typography
        data-testid="payer-task-summary"
        sx={{ ...whatsHappeningSx, ...(!body && clampSx) }}
      >
        <LinkifiedText text={summary} onLinkClick={() => onLinkClick?.('inline')} />
      </Typography>
      {body && (
        <Typography
          data-testid="payer-task-body"
          sx={{ ...whatsHappeningSx, whiteSpace: 'pre-line', mt: '4px', ...clampSx }}
        >
          <LinkifiedText text={body} onLinkClick={() => onLinkClick?.('inline')} />
        </Typography>
      )}
      {clampable && (
        <ButtonBase
          onClick={() => {
            const next = !expanded;
            setExpanded(next);
            onToggle?.(next);
          }}
          aria-expanded={expanded}
          sx={{ ...taskLinkSx, fontSize: '12px', color: 'primary.main', mt: '2px' }}
        >
          {expanded ? SHOW_LESS_LABEL : SHOW_MORE_LABEL}
        </ButtonBase>
      )}
      {attachedLinks.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: '4px', p: 0 }}>
          {attachedLinks.map(link => (
            <Box component="li" key={link.href} sx={{ fontSize: '13px', lineHeight: 1.55 }}>
              <MuiLink
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onLinkClick?.('attached')}
                sx={taskLinkSx}
              >
                {link.label}
              </MuiLink>
            </Box>
          ))}
        </Box>
      )}
      {task.remainingCount > 0 && (
        <Typography sx={{ fontSize: '12px', color: '#8a8a96', mt: '4px' }}>
          {moreTasksLabel(task.remainingCount)}
        </Typography>
      )}
    </Box>
  );
}

/**
 * One row of the clinic Payer Connections table (AC-6), in the Payer Handoff
 * layout: the payer and its rails, who has it, what's happening plus the
 * row's action, and how long it has been waiting. Presentational only —
 * expects a `<TableBody>` ancestor.
 */
export function PayerRow({
  payer,
  action,
  audience,
  asOf,
  onTaskLinkClick,
  onTaskToggled,
}: PayerRowProps) {
  const rails = Object.values(payer.rails).filter((rail): rail is RailView => Boolean(rail));
  return (
    <TableRow>
      <TableCell sx={{ ...cellSx, minWidth: 0 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1c1c22' }}>
          {payer.payerName}
        </Typography>
        {rails.length > 0 && (
          <Typography sx={{ fontSize: '12px', color: '#8a8a96', mt: '3px' }}>
            {railsLine(rails, audience)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={cellSx}>
        <StateChip state={payer.state} audience={audience} />
      </TableCell>
      <TableCell sx={{ ...cellSx, minWidth: 0 }}>
        {payer.openTask ? (
          <OpenTaskCopy
            key={payer.openTask.taskId}
            summary={payer.whatsHappening}
            task={payer.openTask}
            onLinkClick={onTaskLinkClick && (source => onTaskLinkClick(payer, source))}
            onToggle={onTaskToggled && (expanded => onTaskToggled(payer, expanded))}
          />
        ) : (
          <Typography sx={whatsHappeningSx}>{payer.whatsHappening}</Typography>
        )}
        {action && <Box sx={{ mt: '6px' }}>{action}</Box>}
      </TableCell>
      {audience === 'staff' && (
        <TableCell sx={{ ...cellSx, minWidth: 220 }}>
          <StediStatusList
            statuses={payer.stediStatuses}
            ariaLabel={`Stedi statuses for ${payer.payerName}`}
          />
        </TableCell>
      )}
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
        {asOf && (
          <StatusAgeLine
            statusUpdatedAt={payer.selectedRail?.statusUpdatedAt}
            asOf={asOf}
            state={payer.state}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
